import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DEFAULT_TABLE_EXTRACTION_SPEC_STATE } from '~/features/x-ray/constants';
import { PromptModelTestCase } from '~/features/x-ray/types/deprecated';
import { ModelVersion } from '~/features/x-ray/types/model';
import { Nullable } from '~/types/Nullable';

import { SliceType } from '../types';
import { State } from './types';

export const initialState: State = {
  activeModel: null,
  activeModelResultInfo: null,
  activeTableModelVersion: null,
  activeTestcaseNumber: 1,
  furthestNavigatedTestCaseNumber: 1,
  activeVersionNumber: 1,
  configuration: {
    field: null,
    query: [],
    instructions: null,
    internal: null,
  },
  creditsSummary: null,
  dismissedIncorrectStatusMessage: false,
  documentViewer: {},
  hasViewedWelcomeContent: false,
  isNewVersion: false,
  modelAction: null,
  modelVersions: [],
  optimize: null,
  publishMode: 'all_docs_in_scope',
  redirect: null,
  reviewIncorrectCount: 0,
  tableConfiguration: {
    query: [],
    tableSpecState: DEFAULT_TABLE_EXTRACTION_SPEC_STATE,
  },
};

const initialFieldClassification: State['configuration']['fieldClassification'] =
  {
    isMulti: false,
    options: [],
  };

const insertTestCaseInSortedOrder = (
  testCases: PromptModelTestCase[],
  newTestCase: PromptModelTestCase,
): PromptModelTestCase[] => {
  if (testCases.some((tc) => tc.number === newTestCase.number)) {
    return testCases;
  }
  return [...testCases, newTestCase].sort((a, b) => a.number - b.number);
};

export default createSlice({
  name: SliceType.XRay,
  initialState,
  selectors: {
    selectActiveModel: (state) => state.activeModel,
    selectActiveModelResultInfo: (state) => state.activeModelResultInfo,
    selectActiveTableModelVersion: (state) => state.activeTableModelVersion,
    selectActiveTestcaseNumber: (state) => state.activeTestcaseNumber,
    selectFurthestNavigatedTestCaseNumber: (state) =>
      state.furthestNavigatedTestCaseNumber,
    selectActiveVersionNumber: (state) => state.activeVersionNumber,
    selectConfiguration: (state) => state.configuration,
    selectCreditsSummary: (state) => state.creditsSummary,
    selectDismissedIncorrectStatusMessage: (state) =>
      state.dismissedIncorrectStatusMessage,
    selectDocumentViewer: (state) => state.documentViewer,
    selectField: (state) => state.configuration.field,
    selectHasViewedWelcomeContent: (state) => state.hasViewedWelcomeContent,
    selectInstructions: (state) => state.configuration.instructions,
    selectIsNewVersion: (state) => state.isNewVersion,
    selectIsViewingPastVersion: ({ modelVersions, activeVersionNumber }) =>
      activeVersionNumber !== modelVersions.length,
    selectModelAction: (state) => state.modelAction,
    selectModelVersions: (state) => state.modelVersions,
    selectOptimize: (state) => state.optimize,
    selectPublishMode: (state) => state.publishMode,
    selectQuery: (state) => state.configuration.query,
    selectRedirect: (state) => state.redirect,
    selectReviewIncorrectCount: (state) => state.reviewIncorrectCount,
    selectTableConfiguration: (state) => state.tableConfiguration,
  },
  reducers: {
    addTableTestCase: (state, action: PayloadAction<PromptModelTestCase>) => {
      if (state.activeTableModelVersion) {
        state.activeTableModelVersion.testCases = insertTestCaseInSortedOrder(
          state.activeTableModelVersion.testCases,
          action.payload,
        );
      }
    },
    addTestCase: (state, action: PayloadAction<PromptModelTestCase>) => {
      if (state.activeModel) {
        state.activeModel.testCases = insertTestCaseInSortedOrder(
          state.activeModel.testCases,
          action.payload,
        );
      }
    },
    resetXRay: () => initialState,
    setActiveModel: (state, action: PayloadAction<State['activeModel']>) => {
      state.activeModel = action.payload;
    },
    setActiveModelResultInfo: (
      state: State,
      action: PayloadAction<State['activeModelResultInfo']>,
    ) => {
      state.activeModelResultInfo = action.payload;
    },
    setActiveTableModelVersion: (
      state: State,
      action: PayloadAction<Nullable<ModelVersion>>,
    ) => {
      state.activeTableModelVersion = action.payload;
    },
    setActiveTestcaseNumber: (state: State, action: PayloadAction<number>) => {
      state.activeTestcaseNumber = action.payload;
      state.furthestNavigatedTestCaseNumber = Math.max(
        state.activeTestcaseNumber,
        state.furthestNavigatedTestCaseNumber,
      );
    },
    setActiveVersionNumber: (state: State, action: PayloadAction<number>) => {
      state.activeVersionNumber = action.payload;
    },
    setConfiguration: (
      state: State,
      action: PayloadAction<State['configuration']>,
    ) => {
      state.configuration = action.payload;
    },
    setCreditsSummary: (
      state: State,
      action: PayloadAction<State['creditsSummary']>,
    ) => {
      state.creditsSummary = action.payload;
    },
    setDefaultOption: (
      state,
      action: PayloadAction<
        NonNullable<
          State['configuration']['fieldClassification']
        >['defaultOption']
      >,
    ) => {
      if (!state.configuration.fieldClassification) {
        state.configuration.fieldClassification = initialFieldClassification;
      }
      state.configuration.fieldClassification.defaultOption = action.payload;
    },
    setDismissedIncorrectStatusMessage: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.dismissedIncorrectStatusMessage = action.payload;
    },
    setDocumentViewer: (
      state,
      action: PayloadAction<State['documentViewer']>,
    ) => {
      state.documentViewer = action.payload;
    },
    setField: (
      state: State,
      action: PayloadAction<{
        field: State['configuration']['field'];
        fieldClassification?: State['configuration']['fieldClassification'];
      }>,
    ) => {
      state.configuration.field = action.payload.field;
      state.configuration.fieldClassification =
        action.payload.fieldClassification;
    },
    setHasViewedWelcomeContent: (state, action: PayloadAction<boolean>) => {
      state.hasViewedWelcomeContent = action.payload;
    },
    setInstructions: (
      state: State,
      action: PayloadAction<State['configuration']['instructions']>,
    ) => {
      state.configuration.instructions = action.payload;
    },
    setIsNewVersion: (state, action: PayloadAction<boolean>) => {
      state.isNewVersion = action.payload;
    },
    setIsOpenEnded: (
      state,
      action: PayloadAction<
        NonNullable<
          State['configuration']['fieldClassification']
        >['isOpenEnded']
      >,
    ) => {
      if (!state.configuration.fieldClassification) {
        state.configuration.fieldClassification = initialFieldClassification;
      }
      state.configuration.fieldClassification.isOpenEnded = action.payload;
    },
    setModelAction: (state, action: PayloadAction<State['modelAction']>) => {
      state.modelAction = action.payload;
    },
    setModelVersions: (
      state,
      action: PayloadAction<State['modelVersions']>,
    ) => {
      state.modelVersions = action.payload;
    },
    setOptimize: (state, action: PayloadAction<State['optimize']>) => {
      state.optimize = action.payload;
    },
    setOptions: (
      state,
      action: PayloadAction<
        NonNullable<State['configuration']['fieldClassification']>['options']
      >,
    ) => {
      if (!state.configuration.fieldClassification) {
        state.configuration.fieldClassification = initialFieldClassification;
      }
      state.configuration.fieldClassification.options = action.payload;
    },
    setPublishMode: (state, action: PayloadAction<State['publishMode']>) => {
      state.publishMode = action.payload;
    },
    setQuery: (
      state,
      action: PayloadAction<State['configuration']['query']>,
    ) => {
      state.configuration.query = action.payload;
    },
    setRedirect: (state, action: PayloadAction<State['redirect']>) => {
      state.redirect = action.payload;
    },
    setReviewIncorrectCount: (
      state,
      action: PayloadAction<State['reviewIncorrectCount']>,
    ) => {
      state.reviewIncorrectCount = action.payload;
    },
    setTableConfiguration: (
      state,
      action: PayloadAction<State['tableConfiguration']>,
    ) => {
      state.tableConfiguration = action.payload;
    },
    setTableTestCases: (
      state: State,
      action: PayloadAction<PromptModelTestCase[]>,
    ) => {
      if (state.activeTableModelVersion) {
        state.activeTableModelVersion.testCases = action.payload;
      }
    },
    setTestCases: (
      state: State,
      action: PayloadAction<PromptModelTestCase[]>,
    ) => {
      if (state.activeModel) {
        state.activeModel.testCases = action.payload;
      }
    },
    setUpdatedTableTestCase: (
      state: State,
      action: PayloadAction<PromptModelTestCase>,
    ) => {
      if (state.activeTableModelVersion) {
        const { testCases } = state.activeTableModelVersion;
        const updatedTestCase = action.payload;
        state.activeTableModelVersion.testCases = testCases.map((testCase) => {
          if (testCase.number === updatedTestCase.number) {
            return updatedTestCase;
          }
          return testCase;
        });
      }
    },
    setUpdatedTestCase: (
      state: State,
      action: PayloadAction<PromptModelTestCase>,
    ) => {
      if (state.activeModel) {
        const { testCases } = state.activeModel;
        const updatedTestCase = action.payload;
        state.activeModel.testCases = testCases.map((testCase) => {
          if (testCase.number === updatedTestCase.number) {
            return updatedTestCase;
          }
          return testCase;
        });
      }
    },
  },
});
